import { useState, useCallback, useRef, useEffect } from "react";
import { Mail, User, Phone, AlignLeft, Send, CheckCircle, AlertCircle, ShieldCheck } from "lucide-react";
import ServerCaptchaWidget from "./ServerCaptchaWidget";
import "../styles/contactPage.css";
import apiFetch from "../apiFetch";

const MAX_NAME_CHARS = 100;
const MAX_DESC_WORDS = 200;
const MAX_DESC_CHARS = 1400;

function countWords(str) {
  return str.trim() === "" ? 0 : str.trim().split(/\s+/).length;
}

// Client-side mirrors of server validation rules
function validateName(v) {
  if (!v.trim()) return "Name is required.";
  if (v.trim().length < 3) return "Name must be at least 3 characters.";
  if (v.trim().length > MAX_NAME_CHARS) return `Name must be ${MAX_NAME_CHARS} characters or fewer.`;
  if (/[0-9]/.test(v)) return "Name must not contain numbers.";
  if (/[^A-Za-z\s'\-]/.test(v)) return "Name must not contain special characters.";
  return null;
}

function validateEmail(v) {
  if (!v.trim()) return "Email address is required.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim())) return "Please enter a valid email address.";
  return null;
}

function validatePhone(v) {
  if (!v.trim()) return null; // optional
  if (!/^[0-9\s+\-().]{1,20}$/.test(v.trim())) return "Phone number contains invalid characters.";
  if (v.trim().replace(/\D/g, "").length < 6) return "Phone number must contain at least 6 digits.";
  return null;
}

function validateDescription(v) {
  if (!v.trim()) return "Description is required.";
  if (v.trim().length < 10) return "Description must be at least 10 characters.";
  if (/<script/i.test(v) || /javascript\s*:/i.test(v) || /on\w+\s*=/i.test(v)) {
    return "Description contains disallowed content.";
  }
  if (countWords(v) > MAX_DESC_WORDS) return `Description must be ${MAX_DESC_WORDS} words or fewer.`;
  if (v.trim().length > MAX_DESC_CHARS) return "Description is too long.";
  return null;
}

const validators = {
  name: validateName,
  email: validateEmail,
  phone: validatePhone,
  description: validateDescription,
};

export default function ContactPage() {
  const [fields, setFields] = useState({ name: "", phone: "", email: "", description: "" });
  const [honeypot, setHoneypot] = useState("");
  const [formLoadedAt, setFormLoadedAt] = useState(null);
  const [touched, setTouched] = useState({});

  useEffect(() => { setFormLoadedAt(Date.now()); }, []);
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState(null); // { type: "success"|"failure", message }
  const [captchaOk, setCaptchaOk] = useState(false);
  const [captchaTouched, setCaptchaTouched] = useState(false);
  const captchaRef = useRef(null);

  const errors = {
    name: validators.name(fields.name),
    email: validators.email(fields.email),
    phone: validators.phone(fields.phone),
    description: validators.description(fields.description),
  };

  const hasErrors = Object.values(errors).some(Boolean);
  const wordCount = countWords(fields.description);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFields((f) => ({ ...f, [name]: value }));
  }, []);

  const handleBlur = useCallback((e) => {
    setTouched((t) => ({ ...t, [e.target.name]: true }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, phone: true, description: true });
    setCaptchaTouched(true);
    if (hasErrors || !captchaOk) return;

    setBanner(null);
    setSubmitting(true);

    try {
      const { token: captchaToken, answer: captchaAnswer } = captchaRef.current?.getChallenge() ?? {};
      const res = await apiFetch(`/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...fields, website: honeypot, formLoadedAt, captchaToken, captchaAnswer }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setBanner({ type: "success", message: data.message });
        setFields({ name: "", phone: "", email: "", description: "" });
        setTouched({});
        setCaptchaOk(false);
        setCaptchaTouched(false);
        captchaRef.current?.refresh();
      } else {
        setBanner({ type: "failure", message: data.message || "Something went wrong. Please try again." });
      }
    } catch {
      setBanner({ type: "failure", message: "Could not reach the server. Please check your connection and try again." });
    } finally {
      setSubmitting(false);
    }
  };

  function fieldClass(name, base) {
    return touched[name] && errors[name] ? `${base} error` : base;
  }

  return (
    <div className="contact-page">
      <div className="contact-container">

        {/* ── Hero ── */}
        <div className="contact-hero">
          <div className="contact-hero-icon">
            <Mail size={26} color="#fff" />
          </div>
          <div className="contact-hero-text">
            <h1>Contact Us</h1>
            <p className="contact-hero-sub">
              Have a question or feedback? Fill in the form and we'll get back to you.
            </p>
          </div>
        </div>

        {/* ── Banner ── */}
        {banner && (
          <div className={`contact-banner ${banner.type}`}>
            {banner.type === "success"
              ? <CheckCircle size={18} />
              : <AlertCircle size={18} />}
            <span>{banner.message}</span>
          </div>
        )}

        {/* ── Form ── */}
        <div className="contact-card">
          <form onSubmit={handleSubmit} noValidate>

            {/* Honeypot — hidden from real users, bots fill it in */}
            <div className="contact-hp-field" aria-hidden="true">
              <label htmlFor="website">Website (leave blank)</label>
              <input
                id="website"
                name="website"
                type="text"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            {/* Name + Email row */}
            <div className="contact-row">
              <div className="contact-field">
                <label className="contact-label" htmlFor="name">
                  <User size={13} /> Full Name <span className="contact-required">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  placeholder="Jane Smith"
                  maxLength={MAX_NAME_CHARS}
                  value={fields.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={fieldClass("name", "contact-input")}
                />
                {touched.name && errors.name && (
                  <p className="contact-error"><AlertCircle size={12} />{errors.name}</p>
                )}
              </div>

              <div className="contact-field">
                <label className="contact-label" htmlFor="email">
                  <Mail size={13} /> Email Address <span className="contact-required">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="jane@example.com"
                  value={fields.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={fieldClass("email", "contact-input")}
                />
                {touched.email && errors.email && (
                  <p className="contact-error"><AlertCircle size={12} />{errors.email}</p>
                )}
              </div>
            </div>

            {/* Phone */}
            <div className="contact-field">
              <label className="contact-label" htmlFor="phone">
                <Phone size={13} /> Phone Number <span style={{ color: "#9ca3af", fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                placeholder="+44 7700 900000"
                maxLength={20}
                value={fields.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                className={fieldClass("phone", "contact-input")}
              />
              {touched.phone && errors.phone && (
                <p className="contact-error"><AlertCircle size={12} />{errors.phone}</p>
              )}
            </div>

            {/* Description */}
            <div className="contact-field">
              <label className="contact-label" htmlFor="description">
                <AlignLeft size={14} /> Your Enquiry <span className="contact-required">*</span>
              </label>
              <textarea
                id="description"
                name="description"
                placeholder="Describe your enquiry in up to 200 words…"
                maxLength={MAX_DESC_CHARS}
                value={fields.description}
                onChange={handleChange}
                onBlur={handleBlur}
                className={fieldClass("description", "contact-textarea")}
              />
              <div className="contact-hint">
                <span className="contact-hint-text">Plain text only — no HTML or scripts.</span>
                <span className={`contact-word-count${wordCount > MAX_DESC_WORDS ? " over" : ""}`}>
                  {wordCount} / {MAX_DESC_WORDS} words
                </span>
              </div>
              {touched.description && errors.description && (
                <p className="contact-error"><AlertCircle size={12} />{errors.description}</p>
              )}
            </div>

            {/* CAPTCHA */}
            <div className="contact-field">
              <div className="contact-label">
                <ShieldCheck size={14} /> Security Check <span className="contact-required">*</span>
              </div>
              <div className="contact-captcha-wrap">
                <ServerCaptchaWidget
                  ref={captchaRef}
                  onVerify={(ok) => { setCaptchaOk(ok); if (ok) setCaptchaTouched(true); }}
                />
              </div>
              {captchaTouched && !captchaOk && (
                <p className="contact-error"><AlertCircle size={12} />Please complete the security check.</p>
              )}
            </div>

            <button type="submit" className="contact-submit" disabled={submitting}>
              {submitting
                ? <><div className="contact-spinner" /> Sending…</>
                : <><Send size={16} /> Send Message</>}
            </button>

          </form>
        </div>

      </div>
    </div>
  );
}
